import React from 'react';
import { InlineSpinner } from '../../common/loading';

import {
  Box,
  Typography,
  TextField,
  Grid,
  FormHelperText,
  Button,
  Autocomplete,
alpha,
  Chip,
} from '@mui/material';
import { AttachFile as AttachFileIcon } from '@mui/icons-material';
import { SEARCE_COLORS } from '../../../theme';
import { ProjectDropdownOption, TeamMemberOption } from '../../../services/api';
import { FormData, LevelType } from './types';
import { getProjectStatusColor, filterProjectsByStatus } from './utils';

interface AppreciationFormProps {
  formData: FormData;
  onInputChange: (field: keyof FormData, value: string | number | null | File) => void;
  selectedLevel: LevelType;
  userEmail: string;
  projectOptions: ProjectDropdownOption[];
  projectsLoading: boolean;
  projectStatusFilter: string | null;
  onProjectStatusFilterChange: (filter: string | null) => void;
  selectedProject: ProjectDropdownOption | null;
  onProjectSelect: (project: ProjectDropdownOption | null) => void;
  teamMemberOptions: TeamMemberOption[];
  teamMembersLoading: boolean;
  selectedTeamMembers: TeamMemberOption[];
  onTeamMemberSelect: (members: TeamMemberOption[]) => void;
  isFormDisabled: boolean;
}

export const AppreciationForm: React.FC<AppreciationFormProps> = ({
  formData,
  onInputChange,
  selectedLevel,
  userEmail,
  projectOptions,
  projectsLoading,
  projectStatusFilter,
  onProjectStatusFilterChange,
  selectedProject,
  onProjectSelect,
  teamMemberOptions,
  teamMembersLoading,
  selectedTeamMembers,
  onTeamMemberSelect,
  isFormDisabled,
}) => {
  const filteredProjectOptions = filterProjectsByStatus(projectOptions, projectStatusFilter);

  const textFieldSx = {
    '& .MuiOutlinedInput-root': {
      borderRadius: 2,
      backgroundColor: isFormDisabled ? alpha(SEARCE_COLORS.neutral_200, 0.5) : SEARCE_COLORS.cardBackground,
      transition: 'background-color 0.2s ease',
      '&:hover .MuiOutlinedInput-notchedOutline': {
        borderColor: isFormDisabled ? SEARCE_COLORS.border : SEARCE_COLORS.primary,
      },
      '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
        borderColor: SEARCE_COLORS.primary,
      },
      '&.Mui-disabled': {
        backgroundColor: alpha(SEARCE_COLORS.neutral_200, 0.3),
      },
    },
    '& .MuiInputLabel-root.Mui-focused': {
      color: SEARCE_COLORS.primary,
    },
  };

  return (
    <Grid container spacing={3}>
      {/* Email - Auto-filled from logged-in user */}
      <Grid item xs={12}>
        <TextField
          fullWidth
          label="Email"
          required
          type="email"
          value={userEmail}
          disabled
          helperText="Your logged-in email address"
          sx={{
            ...textFieldSx,
            '& .MuiInputBase-input.Mui-disabled': {
              WebkitTextFillColor: SEARCE_COLORS.textPrimary,
              opacity: 0.8,
            },
          }}
        />
      </Grid>

      {/* Project Code/Opportunity ID - Dynamic Dropdown */}
      <Grid item xs={12}>
        <Box sx={{ mb: 1.5 }}>
          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.75 }}>
            Filter by project status
          </Typography>
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.75 }}>
            {['All', 'Closed', 'In progress', 'Yet to Start'].map((status) => {
              const isAll = status === 'All';
              const isSelected = isAll ? !projectStatusFilter : projectStatusFilter === status;
              return (
                <Chip
                  key={status}
                  label={status}
                  size="small"
                  onClick={() => onProjectStatusFilterChange(isAll ? null : (projectStatusFilter === status ? null : status))}
                  sx={{
                    backgroundColor: isSelected ? alpha(SEARCE_COLORS.primary, 0.15) : undefined,
                    color: isSelected ? SEARCE_COLORS.primary : undefined,
                    borderColor: isSelected ? SEARCE_COLORS.primary : SEARCE_COLORS.border,
                    border: '1px solid',
                    '&:hover': {
                      backgroundColor: alpha(SEARCE_COLORS.primary, 0.08),
                    },
                  }}
                />
              );
            })}
          </Box>
        </Box>
        <Autocomplete
          options={filteredProjectOptions}
          getOptionLabel={(option) => option.display_label}
          value={selectedProject}
          onChange={(_, newValue) => onProjectSelect(newValue)}
          loading={projectsLoading}
          isOptionEqualToValue={(option, value) => option.project_code === value.project_code}
          renderInput={(params) => (
            <TextField
              {...params}
              label="Project Code"
              required
              helperText="Select from your projects (where you are the owner)"
              sx={textFieldSx}
              InputProps={{
                ...params.InputProps,
                endAdornment: (
                  <>
                    {projectsLoading ? <InlineSpinner size={20} /> : null}
                    {params.InputProps.endAdornment}
                  </>
                ),
              }}
            />
          )}
          renderOption={(props, option) => {
            const statusColor = getProjectStatusColor(option.status);
            const statusLabel = option.status || 'Unknown';
            return (
              <li {...props} key={option.project_code}>
                <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1.5, width: '100%' }}>
                  <Box sx={{ flex: 1, minWidth: 0 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
                      <Typography variant="body2" fontWeight={500}>
                        {option.project_code}
                      </Typography>
                      <Box sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.5 }}>
                        <Box
                          sx={{
                            width: 8,
                            height: 8,
                            borderRadius: '50%',
                            backgroundColor: statusColor,
                            flexShrink: 0,
                          }}
                        />
                        <Typography variant="caption" sx={{ color: statusColor, fontWeight: 500 }}>
                          {statusLabel}
                        </Typography>
                      </Box>
                    </Box>
                    <Typography variant="caption" color="text.secondary">
                      {option.project_name} {option.account ? `• ${option.account}` : ''}
                    </Typography>
                  </Box>
                </Box>
              </li>
            );
          }}
          noOptionsText={projectsLoading ? "Loading projects..." : "No projects found where you are the owner"}
        />
      </Grid>

      <Grid item xs={12}>
        <TextField
          fullWidth
          label="Customer SPOC Name"
          required
          value={formData.customerSpocName}
          onChange={(e) => onInputChange('customerSpocName', e.target.value)}
          helperText="Name of the person who has provided the feedback"
          sx={textFieldSx}
        />
      </Grid>

      <Grid item xs={12}>
        <TextField
          fullWidth
          label="Customer SPOC Designation"
          value={formData.customerSpocDesignation}
          onChange={(e) => onInputChange('customerSpocDesignation', e.target.value)}
          helperText="Please enter the Customer SPOC designation"
          sx={textFieldSx}
        />
      </Grid>

      {/* Project Owner Name - Auto-filled from selected project */}
      <Grid item xs={12}>
        <TextField
          fullWidth
          label="Project Owner Name *"
          value={formData.projectOwnerName}
          disabled
          helperText="Auto-filled from the selected project"
          sx={{
            ...textFieldSx,
            '& .MuiInputBase-input.Mui-disabled': {
              WebkitTextFillColor: SEARCE_COLORS.textPrimary,
              opacity: 0.8,
            },
          }}
        />
      </Grid>

      {selectedLevel === 'individual' && (
        <Grid item xs={12}>
          <Autocomplete
            multiple
            options={teamMemberOptions}
            getOptionLabel={(option) => option.email}
            value={selectedTeamMembers}
            onChange={(_, newValue) => onTeamMemberSelect(newValue)}
            loading={teamMembersLoading}
            disabled={!selectedProject}
            isOptionEqualToValue={(option, value) => option.email === value.email}
            renderInput={(params) => (
              <TextField
                {...params}
                label="Project Member Name *"
                helperText={
                  !selectedProject 
                    ? "Please select a project first" 
                    : "Select team members assigned to this project"
                }
                sx={textFieldSx}
                InputProps={{
                  ...params.InputProps,
                  endAdornment: (
                    <>
                      {teamMembersLoading ? <InlineSpinner size={20} /> : null}
                      {params.InputProps.endAdornment}
                    </>
                  ),
                }}
              />
            )}
            renderOption={(props, option) => (
              <li {...props} key={option.email}>
                <Typography variant="body2">{option.email}</Typography>
              </li>
            )}
            noOptionsText={
              teamMembersLoading 
                ? "Loading team members..." 
                : !selectedProject 
                  ? "Select a project first" 
                  : "No team members found for this project"
            }
          />
        </Grid>
      )}

      <Grid item xs={12}>
        <TextField
          fullWidth
          label="Feedback Snippet"
          required
          multiline
          rows={4}
          value={formData.feedbackSnippet}
          onChange={(e) => onInputChange('feedbackSnippet', e.target.value)}
          helperText="Please paste a short snippet of the feedback received by the customer in this section."
          sx={textFieldSx}
        />
      </Grid>

      <Grid item xs={12}>
        <Typography
          variant="body2"
          sx={{ fontWeight: 500, color: SEARCE_COLORS.textPrimary, mb: 0.5 }}
        >
          Upload Customer Feedback <span style={{ color: SEARCE_COLORS.error }}>*</span>
        </Typography>
        <FormHelperText sx={{ mt: 0, mb: 1.5 }}>
          Please make a PDF Copy of the Feedback received on email and upload the file here
        </FormHelperText>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          <Button
            variant="outlined"
            component="label"
            startIcon={<AttachFileIcon />}
            sx={{
              borderColor: formData.appreciationFile ? SEARCE_COLORS.secondary : SEARCE_COLORS.border,
              color: formData.appreciationFile ? SEARCE_COLORS.secondary : SEARCE_COLORS.textSecondary,
              textTransform: 'none',
              '&:hover': {
                borderColor: SEARCE_COLORS.primary,
                backgroundColor: alpha(SEARCE_COLORS.primary, 0.04),
              },
            }}
          >
            {formData.appreciationFile ? 'Change file' : 'Add file'}
            <input
              type="file"
              hidden
              accept=".pdf,.doc,.docx,.png,.jpg,.jpeg"
              onChange={(e) => {
                const file = e.target.files?.[0] || null;
                onInputChange('appreciationFile', file);
              }}
            />
          </Button>
          <Typography 
            variant="body2" 
            sx={{ 
              color: formData.appreciationFile ? SEARCE_COLORS.textPrimary : SEARCE_COLORS.textSecondary,
              fontWeight: formData.appreciationFile ? 500 : 400,
            }}
          >
            {formData.appreciationFile ? formData.appreciationFile.name : 'No file chosen'}
          </Typography>
        </Box>
        <FormHelperText sx={{ mt: 1 }}>
          Supported formats: PDF, DOC, DOCX, PNG, JPG, JPEG (Max 10MB)
        </FormHelperText>
      </Grid>
    </Grid>
  );
};
